/** @jsxImportSource @emotion/react */
import {
  type ErrorResponse,
  formatErrorResponse,
} from '@quickbit/backoffice-core-frontend';
import {
  Checkbox,
  Input,
  Modal,
  Select,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useAsyncFn } from 'react-use';
import {
  getOffboarding,
  offboardUser,
  OffboardingPayoutMethod,
  OffboardingState,
} from 'tabs/AppUsers/api';

const METHOD_LABELS: Record<OffboardingPayoutMethod, string> = {
  [OffboardingPayoutMethod.KRAKEN_CRYPTO]: 'Crypto sent from Kraken',
  [OffboardingPayoutMethod.BANK_TRANSFER]: 'Bank transfer',
};

interface PayoutDetails {
  destination: string;
  reference: string;
}

const EMPTY_DETAILS: PayoutDetails = { destination: '', reference: '' };

interface Props {
  userId: string;
  canEdit: boolean;
  onOffboarded: () => void;
}

/** Records that a customer's balances were paid out by hand and closes the
 * account. */
export const OffboardCustomer = ({ userId, canEdit, onOffboarded }: Props) => {
  const [open, setOpen] = useState(false);
  const [{ value: state, loading }, load] = useAsyncFn(
    () => getOffboarding(userId),
    [userId]
  );
  useEffect(() => {
    void load();
  }, [load]);

  const offboarded = !!state?.offboarding;
  return (
    <>
      <UtilityButton
        text={offboarded ? 'Offboarding record' : 'Offboard customer'}
        onClick={() => {
          void load();
          setOpen(true);
        }}
        loading={loading}
        disabled={!state || (!offboarded && !canEdit)}
      />
      {open && state && (
        <OffboardModal
          userId={userId}
          state={state}
          onClose={() => setOpen(false)}
          onRejected={() => void load()}
          onOffboarded={() => {
            void load();
            onOffboarded();
          }}
        />
      )}
    </>
  );
};

interface ModalProps {
  userId: string;
  state: OffboardingState;
  onClose: () => void;
  onRejected: () => void;
  onOffboarded: () => void;
}

const OffboardModal = ({
  userId,
  state,
  onClose,
  onRejected,
  onOffboarded,
}: ModalProps) => {
  const { spacing, palette } = useTheme();
  const [methods, setMethods] = useState<
    Partial<Record<string, OffboardingPayoutMethod>>
  >({});
  const methodOf = (walletId: string) =>
    methods[walletId] ?? OffboardingPayoutMethod.KRAKEN_CRYPTO;
  const [checkedSends, setCheckedSends] = useState<string[]>([]);
  const [crypto, setCrypto] = useState<Partial<Record<string, PayoutDetails>>>(
    {}
  );
  const [bank, setBank] = useState<PayoutDetails>(EMPTY_DETAILS);
  const [note, setNote] = useState('');

  const [{ loading, error }, submit] = useAsyncFn(async () => {
    try {
      await offboardUser(
        userId,
        state.wallets.map((w) => {
          const method = methodOf(w.walletId);
          const details =
            method === OffboardingPayoutMethod.BANK_TRANSFER
              ? bank
              : crypto[w.walletId] ?? EMPTY_DETAILS;
          return {
            walletId: w.walletId,
            expectedBalance: w.balance,
            method,
            destination: details.destination,
            payoutReference: details.reference,
          };
        }),
        note,
        checkedSends
      );
    } catch (e) {
      onRejected();
      throw e;
    }
    onOffboarded();
    onClose();
  }, [userId, state, methods, crypto, bank, note, checkedSends]);

  if (state.offboarding) {
    const { createdAt, createdBy, note, payouts } = state.offboarding;
    return (
      <Modal
        title="Offboarding record"
        subtitle={`Offboarded ${new Date(
          createdAt
        ).toLocaleString()} by ${createdBy}`}
        closeModal={onClose}
        secondaryButtonProps={{ onClick: onClose, text: 'Close' }}
      >
        <div css={{ display: 'grid', gap: spacing.xs, minWidth: '600px' }}>
          {payouts.length === 0 && (
            <Typography variant="body">
              There was nothing to pay out.
            </Typography>
          )}
          {payouts.map((p) => (
            <div key={p.currency}>
              <Typography variant="bodyBold">
                {p.amount} {p.currency}: {METHOD_LABELS[p.method]}
              </Typography>
              <Typography variant="bodySmall">To {p.destination}</Typography>
              <Typography variant="bodySmall">
                Reference {p.payoutReference}
              </Typography>
            </div>
          ))}
          {note && <Typography variant="bodySmall">Note: {note}</Typography>}
        </div>
      </Modal>
    );
  }

  const usesBank = state.wallets.some(
    (w) => methodOf(w.walletId) === OffboardingPayoutMethod.BANK_TRANSFER
  );
  const filled = (d?: PayoutDetails) =>
    !!d && !!d.destination.trim() && !!d.reference.trim();
  const valid =
    state.blockers.length === 0 &&
    state.pendingSends.every((s) => checkedSends.includes(s.transactionId)) &&
    state.wallets.every(
      (w) =>
        methodOf(w.walletId) === OffboardingPayoutMethod.BANK_TRANSFER ||
        filled(crypto[w.walletId])
    ) &&
    (!usesBank || filled(bank));
  const setCryptoField = (
    walletId: string,
    field: keyof PayoutDetails,
    value: string
  ) =>
    setCrypto((prev) => ({
      ...prev,
      [walletId]: { ...(prev[walletId] ?? EMPTY_DETAILS), [field]: value },
    }));

  return (
    <Modal
      title="Offboard customer"
      subtitle="Record how each balance was paid out. This sets every balance to zero and closes the account."
      closeModal={() => !loading && onClose()}
      primaryButtonProps={{
        onClick: () => void submit(),
        text: 'Record payouts and close account',
        loading,
        disabled: !valid || loading,
      }}
      secondaryButtonProps={{
        onClick: onClose,
        text: 'Cancel',
        disabled: loading,
      }}
    >
      <div css={{ display: 'grid', gap: spacing.xs, minWidth: '600px' }}>
        {state.blockers.length > 0 && (
          <div>
            <Typography variant="bodyBold" color={palette.danger.x500}>
              This customer cannot be offboarded yet:
            </Typography>
            {state.blockers.map((b) => (
              <Typography
                key={b}
                variant="bodySmall"
                color={palette.danger.x500}
              >
                {b}
              </Typography>
            ))}
          </div>
        )}
        {state.wallets.length === 0 && (
          <Typography variant="body">
            The customer has no balance to pay out.
          </Typography>
        )}
        {state.pendingSends.length > 0 && (
          <div>
            <Typography variant="bodyBold">
              These sends are still pending. Some pending sends were paid on the
              blockchain without being marked complete, so tick each one only
              after checking it never went out. They will be cancelled, and
              their amount is included in the balances below:
            </Typography>
            {state.pendingSends.map((s) => (
              <div
                key={s.transactionId}
                css={{ display: 'flex', alignItems: 'center' }}
              >
                <Checkbox
                  checked={checkedSends.includes(s.transactionId)}
                  onChange={(checked) =>
                    setCheckedSends((prev) =>
                      checked
                        ? [...prev, s.transactionId]
                        : prev.filter((id) => id !== s.transactionId)
                    )
                  }
                />
                <Typography variant="bodySmall">
                  {s.reference}: {s.amount} {s.currency} from{' '}
                  {new Date(s.createdAt).toLocaleDateString()} never reached the
                  blockchain
                </Typography>
              </div>
            ))}
          </div>
        )}
        {state.wallets.map((w) => (
          <div key={w.walletId} css={{ display: 'grid', gap: spacing.xxs }}>
            <Typography variant="bodyBold">
              {w.balance} {w.currency} (about {w.value} {w.valueCurrency})
            </Typography>
            <Select
              label="Paid by"
              options={Object.values(OffboardingPayoutMethod).map((m) => ({
                label: METHOD_LABELS[m],
                value: m,
              }))}
              values={[methodOf(w.walletId)]}
              onSelect={(s) =>
                setMethods((prev) => ({
                  ...prev,
                  [w.walletId]: s[0] as OffboardingPayoutMethod,
                }))
              }
            />
            {methodOf(w.walletId) === OffboardingPayoutMethod.KRAKEN_CRYPTO && (
              <>
                <Input
                  type="text"
                  label={`${w.currency} address it was sent to`}
                  value={crypto[w.walletId]?.destination ?? ''}
                  onChange={(e) =>
                    setCryptoField(w.walletId, 'destination', e.target.value)
                  }
                />
                <Input
                  type="text"
                  label="Blockchain transaction ID"
                  value={crypto[w.walletId]?.reference ?? ''}
                  onChange={(e) =>
                    setCryptoField(w.walletId, 'reference', e.target.value)
                  }
                />
              </>
            )}
          </div>
        ))}
        {usesBank && (
          <div css={{ display: 'grid', gap: spacing.xxs }}>
            <Typography variant="bodyBold">Bank transfer</Typography>
            <Input
              type="text"
              label="Bank account it was paid to"
              value={bank.destination}
              onChange={(e) =>
                setBank((prev) => ({ ...prev, destination: e.target.value }))
              }
            />
            <Input
              type="text"
              label="Bank payment reference"
              value={bank.reference}
              onChange={(e) =>
                setBank((prev) => ({ ...prev, reference: e.target.value }))
              }
            />
          </div>
        )}
        <Input
          type="textarea"
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />
        {error && (
          <Typography variant="bodySmall" color={palette.danger.x500}>
            {formatErrorResponse(error as unknown as ErrorResponse)}
          </Typography>
        )}
      </div>
    </Modal>
  );
};
